import * as XLSX from 'xlsx';
import { allows, rightsOf, unitsOf } from '../../lib/permissions';
import { formatDate, yearsSince } from '../../lib/format';
import { birthDateOf } from '../patients/patientModel';

// Payments (MongoDB, protected space "Paiements"). The modes keep their former stored values;
// the currency of an amount follows from its mode when it is cash.
export const MODES = [
  { value: 'espèces en :cfa', label: 'Espèces (FCFA)', group: 'Espèces', currency: 'FCFA' },
  { value: 'espèces en :euro', label: 'Espèces (euro)', group: 'Espèces', currency: '€' },
  { value: 'espèces en :dollar', label: 'Espèces (dollar)', group: 'Espèces', currency: '$' },
  { value: 'espèces en :livre', label: 'Espèces (livre)', group: 'Espèces', currency: '£' },
  { value: 'espèces en :dirham', label: 'Espèces (dirham)', group: 'Espèces', currency: 'DH' },
  { value: 'carte :banquaire visa', label: 'Carte bancaire Visa', group: 'Carte' },
  { value: 'cheque :uba', label: 'Chèque UBA', group: 'Chèque' },
  { value: 'Dons', label: 'Don', group: 'Dons' },
];
export const GROUPS = ['Espèces', 'Carte', 'Chèque', 'Dons'];

export const modeOf = (value) => MODES.find((mode) => mode.value === value);
export const modeLabel = (value) => (modeOf(value) ? modeOf(value).label : value || 'Mode non précisé');
export const groupOf = (value) => (modeOf(value) ? modeOf(value).group : 'Autres');

const NUMBER = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });
export const formatAmount = (montant, mode) => {
  const value = Number(montant);
  if (!Number.isFinite(value)) return '—';
  const currency = modeOf(mode) && modeOf(mode).currency;
  return currency ? `${NUMBER.format(value)} ${currency}` : NUMBER.format(value);
};

export const today = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
export const toDateInput = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

// By level, for the payments of the account's units (all of them for the administration);
// the server checks the same rule.
export const paymentAccess = (user) => {
  const rights = rightsOf(user);
  const isAdmin = user.role === 'Admin';
  const myUnits = unitsOf(user.service);
  const owns = (unit) => isAdmin || unitsOf(unit).some((name) => myUnits.includes(name));
  return {
    isAdmin,
    ownsPatient: (patient) => owns(patient && patient.services),
    canAdd: allows(rights, 'payments', 'modifier'),
    canEdit: (payment) => allows(rights, 'payments', 'modifier') && owns(payment.service),
    canDelete: (payment) => allows(rights, 'payments', 'supprimer') && owns(payment.service),
    canExport: allows(rights, 'payments', 'tout'),
  };
};

// The patient as known today, else what the payment kept.
export const patientOf = (payment, patientsById) =>
  patientsById[payment.patientId] || { nom: payment.nom, diagnostic: payment.diagnostic, numeroDeTelephone: payment.numeroDeTelephone, age: payment.age };

export const ageOf = (patient) => {
  const years = yearsSince(birthDateOf(patient));
  return years !== null ? years : patient.age || '';
};

export const searchText = (payment, patient) =>
  [patient.nom, patient.dossierNumber, patient.diagnostic, patient.numeroDeTelephone, payment.ordre, modeLabel(payment.modePaiement), payment.Donnateur, payment.montant, formatDate(payment.datePaiement), payment.service]
    .filter((value) => value !== undefined && value !== null && value !== '')
    .join(' ')
    .toLowerCase();

// Former columns, plus the unit.
export const exportPayments = (rows) => {
  const data = rows.map(({ payment, patient }) => ({
    Ordre: payment.ordre || '',
    Patient: patient.nom || '',
    Diagnostic: patient.diagnostic || '',
    Age: ageOf(patient),
    Téléphone: patient.numeroDeTelephone || '',
    ModePaiement: modeLabel(payment.modePaiement),
    Donnateur: payment.modePaiement === 'Dons' ? payment.Donnateur || '' : '',
    Montant: payment.montant,
    Date: formatDate(payment.datePaiement),
    Unité: payment.service || '',
  }));
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(data), 'Paiements');
  XLSX.writeFile(book, 'paiements.xlsx');
};
